import { HeadlessModal, type ReloadOptions } from "@inertiaui/modal-react";
import type { ReactNode } from "react";
import { useCallback } from "react";

export interface RoutedModalBridgeRenderProps {
  /** Notify Inertia Modal that the leave animation finished so it can unmount. */
  afterLeave: () => void;
  close: () => void;
  emit: (event: string, ...args: unknown[]) => void;
  isOpen: boolean;
  /** Close-only open-change adapter for controlled modal components. */
  onOpenChange: (open: boolean) => void;
  reload: (options?: ReloadOptions) => void;
}

export interface RoutedModalBridgeProps {
  children: (props: RoutedModalBridgeRenderProps) => ReactNode;
}

/**
 * Shared adaptation between Inertia Modal's HeadlessModal render prop and our
 * controlled modal components (system Modal, admin-ui modal primitives).
 * Inertia Modal owns routing, URL state, and the modal stack; `children`
 * renders the actual modal chrome from the adapted props.
 *
 * `children` is invoked inside a real component below HeadlessModal, but it is
 * still a render prop — shells that need their own hooks on these props (e.g.
 * a leave-animation hook) should forward them to an inner component.
 */
export function RoutedModalBridge({ children }: RoutedModalBridgeProps) {
  return (
    <HeadlessModal>
      {(renderProps: {
        isOpen: boolean;
        close: () => void;
        afterLeave: () => void;
        emit: (event: string, ...args: unknown[]) => void;
        reload: (options?: ReloadOptions) => void;
      }) => (
        <RoutedModalBridgeInner
          afterLeave={renderProps.afterLeave}
          close={renderProps.close}
          emit={renderProps.emit}
          isOpen={renderProps.isOpen}
          reload={renderProps.reload}
        >
          {children}
        </RoutedModalBridgeInner>
      )}
    </HeadlessModal>
  );
}

interface RoutedModalBridgeInnerProps extends Omit<RoutedModalBridgeRenderProps, "onOpenChange"> {
  children: RoutedModalBridgeProps["children"];
}

function RoutedModalBridgeInner({ afterLeave, children, close, emit, isOpen, reload }: RoutedModalBridgeInnerProps) {
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close();
    },
    [close],
  );

  return <>{children({ afterLeave, close, emit, isOpen, onOpenChange, reload })}</>;
}
